import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { isPlatformAdmin, useAuthStore } from '@/features/auth';
import { tenantScopedScreen } from '../lib/tenantScopedScreens';
import { useTenantScopeStore } from '../store/tenantScopeStore';

export interface TenantScope {
	/**
	 * Another tenant whose data the screen shows, sent as `?tenantId=` on
	 * reads. `undefined` = the caller's own tenant (nothing is sent).
	 */
	tenantId: string | undefined;
	/**
	 * Showing another tenant: list screens are read-only then (Users and IP
	 * access ignore it, they manage the tenant).
	 */
	readOnly: boolean;
}

/**
 * Tenant the scoped screens show: the one picked in the header, kept across
 * screens. Only platform admins can view another tenant (read-only); for
 * everyone else, for another account's pick and for the caller's own tenant,
 * the scope is empty. UI only: the backend checks the role again.
 */
export function useTenantScope(): TenantScope {
	const user = useAuthStore((state) => state.user);
	const picked = useTenantScopeStore((state) =>
		state.userId === user?.id ? state.tenantId : null,
	);

	const tenantId =
		isPlatformAdmin(user?.role) && picked && picked !== user?.tenantId
			? picked
			: undefined;
	return { tenantId, readOnly: tenantId !== undefined };
}

/**
 * Switch the scoped screens to a tenant (`undefined` = the caller's own).
 * Paging and the current screen's tenant-bound filters
 * (`TENANT_SCOPED_SCREENS`) belong to one tenant, so they are cleared; a
 * screen showing one record of the tenant goes back to its list.
 */
export function useSetTenantScope(): (tenantId: string | undefined) => void {
	const [, setSearchParams] = useSearchParams();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const user = useAuthStore((state) => state.user);
	const setTenant = useTenantScopeStore((state) => state.setTenant);

	return useCallback(
		(tenantId) => {
			if (!user) return;
			setTenant(
				user.id,
				tenantId && tenantId !== user.tenantId ? tenantId : null,
			);
			const screen = tenantScopedScreen(pathname);
			if (screen?.leaveTo) {
				navigate(screen.leaveTo, { replace: true });
				return;
			}
			const resetParams = screen?.resetParams ?? [];
			setSearchParams(
				(previous) => {
					const next = new URLSearchParams(previous);
					next.delete('page');
					for (const param of resetParams) next.delete(param);
					return next;
				},
				{ replace: true },
			);
		},
		[navigate, pathname, setSearchParams, setTenant, user],
	);
}
